import * as React from "react";
import { useNavigate as useRouter } from "../../router";
import { Button } from "../../button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../command";
import { Badge } from "../../badge";
import { ChevronsUpDown, Table2 } from "../../icons";
import { cn } from "../../utils";
import type { DbObjectDto } from "../../api/contracts/database/schema";
import { formatCount, GROUP_LABELS, GROUP_ORDER } from "./format";

/**
 * Quick-jump to any table or view — a grouped `CommandDialog` over the object
 * list, reused across the database surfaces so you can switch tables in place
 * (Overview toolbar, the Table Editor header, the Schema header) instead of
 * walking back to the inventory. Tables open the Table Editor; views / system
 * objects open the Schema detail (matching the inventory's link rules).
 */
export function TableSwitcher({
  objects,
  current,
  trigger = "button",
  className,
}: {
  objects: DbObjectDto[];
  /** When used as a header title (`trigger="title"`), the current object name. */
  current?: string;
  trigger?: "button" | "title";
  className?: string;
}) {
  const router = useRouter();
  const [open, setOpen] = React.useState(false);

  const grouped = React.useMemo(() => {
    const seen = new Set(objects.map((o) => o.group));
    const order = [
      ...GROUP_ORDER.filter((g) => seen.has(g)),
      ...[...seen].filter((g) => !GROUP_ORDER.includes(g)).sort(),
    ];
    return order.map((group) => ({
      group,
      objs: objects.filter((o) => o.group === group),
    }));
  }, [objects]);

  function pick(o: DbObjectDto) {
    setOpen(false);
    if (o.type === "table" && !o.system) {
      router.push(`/database/editor/${encodeURIComponent(o.name)}`);
    } else {
      router.push(`/database/schema/${encodeURIComponent(o.name)}`);
    }
  }

  return (
    <>
      {trigger === "title" ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen(true)}
          className={cn("-ml-2", className)}
        >
          <span className="font-mono type-body font-medium text-foreground">
            {current}
          </span>
          <ChevronsUpDown className="size-3.5 text-muted-foreground" />
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          className={className}
        >
          <Table2 className="size-3.5" /> Jump to table
        </Button>
      )}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Jump to a table or view…" />
        <CommandList>
          <CommandEmpty>No object found.</CommandEmpty>
          {grouped.map(({ group, objs }) => (
            <CommandGroup key={group} heading={GROUP_LABELS[group] ?? group}>
              {objs.map((o) => (
                <CommandItem
                  key={o.name}
                  value={`${o.name} ${group}`}
                  onSelect={() => pick(o)}
                >
                  <Table2 className="size-3.5 text-muted-foreground" />
                  <span className="font-mono">{o.name}</span>
                  {o.system ? (
                    <Badge variant="outline" className="ml-1 text-muted-foreground">
                      system
                    </Badge>
                  ) : null}
                  {o.rowCount >= 0 ? (
                    <span className="ml-auto type-subtext tabular-nums text-muted-foreground">
                      {formatCount(o.rowCount)}
                    </span>
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
